import _ from 'underscore';
import _s from 'underscore.string';
import moment from 'moment';
import { colors, dateFormats, eventTypes } from './constants';
import { replaceOrInsert } from './Etc';
import { distance } from './MapUtil';

export const CLOSE_DISTANCE = 'CLOSE (< 1 mile)';
export const MEDIUM_DISTANCE = 'MEDIUM (1 - 5 miles)';
export const FAR_DISTANCE = 'FAR (> 5 miles)';

function rateLocationChange(oldLocation, newLocation) {
  const kmToMiles = 0.62;

  if (oldLocation && oldLocation.latLng && newLocation && newLocation.latLng) {
    const { latitude: oldX, longitude: oldY } = oldLocation.latLng;
    const { latitude: newX, longitude: newY } = newLocation.latLng;
    const dist = distance(oldX, oldY, newX, newY) * kmToMiles;

    if (dist < 1) {
      return CLOSE_DISTANCE;
    } else if (dist <= 5) {
      return MEDIUM_DISTANCE;
    } else {
      return FAR_DISTANCE;
    }
  } else {
    return 'N/A';
  }
}

const EARLY_MORNING_HOURS = [ 12, 1, 2, 3, 4, 5, 6, 7 ];
const COURT_NAME_REGEX = /(\d+)$/;

export function isGame(event) {
  return event && (event.eventType === eventTypes.GAME || event.eventType === 'game');
}

export function isSchoolGame(event) {
  return event && (event.eventType === eventTypes.SCHOOL_GAME);
}

function isTourneyAppointment(event) {
  return event && (event.eventType === eventTypes.TOURNEY_APPOINTMENT);
}

function isRecruitAppointment(event) {
  return event && (event.eventType === eventTypes.RECRUIT_APPOINTMENT);
}

export function isAppointment(event) {
  return isTourneyAppointment(event) || isRecruitAppointment(event);
}

function getAttendedRecruitsFromOneEvent(event) {
  const { accountUserEventData } = event;

  if (accountUserEventData) {
    return _.chain(accountUserEventData)
      .flatten()
      .where({ attended: true })
      .pluck('event')
      .uniq()
      .pluck('accountRecruits')
      .flatten()
      .uniq()
      .compact()
      .sortBy('lastName')
      .value();
  } else {
    return [];
  }
}

function getAttendedRecruitsFromEvents(events) {
  return _.chain(events.map(getAttendedRecruitsFromOneEvent))
    .flatten()
    .uniq()
    .compact()
    .sortBy('lastName')
    .value();
}

function getAttendingUsersFromOneEvent(event) {
  return _.chain(event.accountUserEventData)
    .where({ attended: true })
    .pluck('userDetails')
    .value();
}

function getAttendingUsersFromEvents(events) {
  return _.chain(events.map(getAttendingUsersFromOneEvent))
    .flatten()
    .uniq()
    .compact()
    .value();
}

export function isFlagged({ event, userId }) {
  let flagged;
  if (event && userId) {
    const { accountUserEventData } = event;

    if (_.isArray(accountUserEventData)) {
      flagged = _.any(accountUserEventData, (aued) => {
        const id = aued.userId || (aued.userDetails && aued.userDetails.id);
        return (aued.flagged && id === userId);
      });
    } else {
      flagged = false;
    }
  } else {
    flagged = false;
  }

  return flagged;
}

export function isFlaggedByAccount(event) {
  if (_.isArray(event.accountUserEventData)) {
    return _.any(event.accountUserEventData, aued => aued.flagged);
  } else {
    return false;
  }
}

export function extractAttendingRecruits(events) {
  if (_.isArray(events)) {
    return getAttendedRecruitsFromEvents(events);
  } else {
    return getAttendedRecruitsFromOneEvent(events);
  }
}
export function extractAttendingUsers(events) {
  if (_.isArray(events)) {
    return getAttendingUsersFromEvents(events);
  } else {
    return getAttendingUsersFromOneEvent(events);
  }
}

export function extractCourtName(event) {
  if (event && event.court) {
    return event.court.name;
  } else {
    return null;
  }
}

export function extractDivisionName(event) {
  if (event && event.gameGroup && event.gameGroup.division) {
    return event.gameGroup.division.name;
  }

  return null;
}

/**
 * Given a string that should represent a time, try to parse it into the DISPLAY_TIME format
 * @param timeString string representing a time
 * @returns {*} string for time in DISPLAY_FORMAT
 */
export function getGameTime(timeString) {
  if (typeof timeString !== 'string') {
    return null;
  }

  const time = moment(timeString, dateFormats.VALID_TIMES);
  if (!time.isValid()) {
    return null;
  }

  const hour = time.hours();
  const includesAM = _s.include(timeString.toUpperCase(), 'AM');
  const includesPM = _s.include(timeString.toUpperCase(), 'PM');

  if (includesAM || includesPM) {
    return time.format(dateFormats.DISPLAY_TIME);
  } else if (_.contains(EARLY_MORNING_HOURS, hour)) {
    return time.add(12, 'h').format(dateFormats.DISPLAY_TIME);
  } else {
    return time.format(dateFormats.DISPLAY_TIME);
  }
}

export function extractTeamName(game, side = 'home') {
  const seed = game[ `${side}Seed` ];
  const team = game[ `${side}Team` ];

  let teamName;

  if (team && team.name) {
    teamName = team.name;
  } else {
    if (seed) {
      teamName = seed.displayName;
    } else {
      teamName = 'TBD';
    }
  }

  return teamName;
}

export function getTourney(event) {
  try {
    if (isGame(event)) {
      return event.gameGroup.division.tourney;
    } else {
      return event.venue.tourney;
    }
  } catch (e) {
    return null;
  }
}

export function getTourneyName(event) {
  const tourney = getTourney(event);
  return tourney ? tourney.name : null;
}

export function extractVenueName(event) {
  try {
    if (isGame(event)) {
      return event.court.venue.name;
    } else {
      return event.venue ? event.venue.name : extractLocationName(event);
    }
  } catch (e) {
    return null;
  }
}

export function isFollowedByUser({ event, userId, team = null }) {
  let followed;

  if (event && userId) {
    const { accountFollowedTeams } = event;

    if (_.isArray(accountFollowedTeams)) {
      followed = _.any(accountFollowedTeams, (followedTeam) => {
        let teamMatch = (team ? followedTeam.team.id === team.id : true);
        let userMatch = (followedTeam.userId === userId);

        return (teamMatch && userMatch);
      });
    } else {
      followed = false;
    }
  } else {
    followed = false;
  }

  return followed;
}

/**
 * Takes a string, trims it, looks for whether it ends in a number, and returns that
 * padded number if it does
 *
 * @param value
 * @returns {*}
 * @constructor
 */
export function lpadNumericesque(value) {
  if (typeof value !== 'string') {
    return '';
  }

  const match = value.trim().match(COURT_NAME_REGEX);

  if (match) {
    return _s.lpad(match[ 0 ], 10, '0');
  } else {
    return value;
  }
}

export function extractAccountData(event) {
  if (event) {
    return event.accountEventData ? event.accountEventData[ 0 ] : null;
  } else {
    return null;
  }
}

export function extractLocationName(event) {
  if (event) {
    const accountEventDatum = extractAccountData(event);

    if (accountEventDatum && accountEventDatum.locationName) {
      return accountEventDatum.locationName;
    } else {
      switch (event.eventType) {
        case eventTypes.RECRUIT_APPOINTMENT:
        case eventTypes.SCHOOL_GAME:
          return event.locationName;

        case eventTypes.TOURNEY_APPOINTMENT:
          const { venue } = event;
          return venue.name;

        case eventTypes.GAME:
          const { court } = event;
          return `${court.name} - ${court.venue.name}`;
      }
    }
  } else {
    return null;
  }
}

export function formatEventAddress(eventAddress) {
  let address = '';
  if (eventAddress.address1) {
    address += eventAddress.address1;
  }
  if (eventAddress.city && address.length > 1) {
    address += `, ${eventAddress.city}`;
  } else {
    if (eventAddress.city) {
      address += eventAddress.city;
    }
  }
  if (eventAddress.province && address.length > 1) {
    address += ` - ${eventAddress.province}`;
  } else {
    if (eventAddress.province) {
      address += eventAddress.province;
    }
  }
  return address;
}

export function extractLocation(event) {
  if (!event) {
    return null;
  }

  let location;
  const accountEventDatum = extractAccountData(event);

  if (accountEventDatum && accountEventDatum.location) {
    return accountEventDatum.location;
  }

  switch (event.eventType) {
    case eventTypes.GAME:
      location = event.court.venue.location;
      break;
    case eventTypes.TOURNEY_APPOINTMENT:
      location = event.venue.location;
      break;
    case eventTypes.SCHOOL_GAME:
    case eventTypes.RECRUIT_APPOINTMENT:
      location = event.location;
      break;
    default:
      location = null;
  }

  return location;
}

export function extractAddress(event) {
  const location = extractLocation(event);

  if (location) {
    return location.address;
  } else {
    return {};
  }
}

export function keywordSearch(events, rawSearch) {
  if (!rawSearch) {
    return events;
  }

  const normalize = (string) => (string ? string.split(' ').join('').toLowerCase() : '');
  const search = normalize(rawSearch);
  const isMatch = (string) => string.indexOf(search) > -1;

  return _.filter(events, event => {
    // match on event name
    const nameMatch = isMatch(normalize(event.name));
    if (nameMatch) {
      return true;
    }

    // match on event locationName
    const locationNameMatch = isMatch(normalize(extractLocationName(event)));
    if (locationNameMatch) {
      return true;
    }

    // match on any recruit name, category, or position
    const recruitMatch = _.any(event.recruits, recruit => {
      const matchesName = isMatch(normalize(recruit.firstName + recruit.lastName));
      const matchesCategory = recruit.category && isMatch(normalize(recruit.category.name));
      const matchesPosition = recruit.position && isMatch(normalize(recruit.position.name));
      const matchesYear = recruit.year && isMatch(recruit.year.toString());
      return (matchesName || matchesCategory || matchesPosition || matchesYear);
    });
    if (recruitMatch) {
      return true;
    }

    // match on address
    const address = extractAddress(event);
    if (address) {
      const { address1, address2, city, province, postalCode } = address;
      if (isMatch(normalize([ address1, address2, city, province, postalCode ].join(', ')))) {
        return true;
      }
    }

    // match on game school name, only for school games
    if (isSchoolGame(event)) {
      const { schools } = event;
      const schoolMatch = _.any(schools, school => isMatch(normalize(school.school.name)));
      if (schoolMatch) {
        return true;
      }
    }

    // if no match, reject event
    return false;
  });
}

export function extractLatLng(event) {
  if (!event) {
    return null;
  }

  const location = extractLocation(event);
  if (!location || !location.latLng) {
    return null;
  }

  const { latitude, longitude } = location.latLng;
  return { lat: latitude, lng: longitude };
}

export function extractTourneyId(event) {
  const tourney = getTourney(event);
  return tourney ? tourney.id : null;
}

export function extractZoneId(event) {
  if (!event) {
    return null;
  }

  switch (event.eventType) {
    case eventTypes.GAME:
      const tourney = getTourney(event);
      return tourney ? tourney.timezone : null;
    case eventTypes.SCHOOL_GAME:
      const locale = extractLocation(event);
      const locationZoneId = locale ? locale.latLng.zoneId : null;
      return event.displayTimezone ? event.displayTimezone : locationZoneId;
    default:
      const location = extractLocation(event);
      if (!location || !location.latLng) {
        return null;
      }

      return location.latLng.zoneId;

  }
}

export function extractRecruits(event) {
  if (!event) {
    return null;
  }

  switch (event.eventType) {
    case eventTypes.GAME:
      return event.accountRecruits;
    case eventTypes.SCHOOL_GAME:
    case eventTypes.RECRUIT_APPOINTMENT:
    case eventTypes.TOURNEY_APPOINTMENT:
      return event.recruits;
    default:
      return null;
  }
}

export function extractFlaggingUsers(event) {
  if (!event) {
    return null;
  } else {
    return _
      .chain(event.accountUserEventData)
      .where({ flagged: true })
      .pluck('userDetails')
      .flatten()
      .value();
  }
}

export function extractScheduled(event) {
  if (event) {
    const accountEventDatum = extractAccountData(event);
    const accountDataScheduled = accountEventDatum && accountEventDatum.scheduled;
    return accountDataScheduled || event.scheduled;
  } else {
    return null;
  }
}

export function extractGameNote(event) {
  if (event) {
    const accountEventDatum = extractAccountData(event);
    const accountDataNote = accountEventDatum && accountEventDatum.note;
    return accountDataNote || event.note;
  } else {
    return null;
  }
}

export function extractLocalDate(event) {
  if (event) {
    if (extractAccountData(event)) {
      return moment(extractScheduled(event))
        .tz(event.displayTimezone || extractZoneId(event) || 'UTC').format(dateFormats.ISO_8601);
    } else {
      return event.localDate;
    }
  } else {
    return null;
  }
}

export function isAccountData(event, ...attrs) {
  if (event) {
    const accountEventDatum = _.first(event.accountEventData) || {};
    return _.any(attrs, attr => Boolean(accountEventDatum[ attr ]));
  } else {
    return null;
  }
}

export function extractTimeTba(event) {
  if (event) {
    return isAccountData(event, 'scheduled') ? false : event.timeTba;
  } else {
    return null;
  }
}

export function updateFlagged({ event, flagged, userId }) {
  const userEventData = _.findWhere(event.accountUserEventData, { userId });
  const update = { ...userEventData, flagged, userId };
  let usersIndexedById = _.indexBy(event.accountUserEventData, 'userId');
  usersIndexedById[ userId ] = update;

  return { ...event, accountUserEventData: _.toArray(usersIndexedById) };
}

export function extractColor(event, flagged) {
  const { FLAGGED_SEASON_EVENT, FLAGGED_TOURNEY_EVENT, UNFLAGGED_SEASON_EVENT, UNFLAGGED_TOURNEY_EVENT } = colors.hex;

  if (event) {
    const firstUserColor = _.chain(event.accountUserEventData)
      .pluck('color')
      .compact()
      .without(FLAGGED_SEASON_EVENT, FLAGGED_TOURNEY_EVENT, UNFLAGGED_SEASON_EVENT, UNFLAGGED_TOURNEY_EVENT)
      .first()
      .value();

    if (firstUserColor) {
      return firstUserColor;
    } else {
      if (flagged) {
        return isSchoolGame(event) ? FLAGGED_SEASON_EVENT : FLAGGED_TOURNEY_EVENT;
      } else {
        return isSchoolGame(event) ? UNFLAGGED_SEASON_EVENT : UNFLAGGED_TOURNEY_EVENT;
      }
    }
  } else {
    return null;
  }
}

export function extractDurationMinutes(event) {
  if (event) {
    const accountEventDatum = extractAccountData(event);
    const accountDataDurationMinutes = accountEventDatum && accountEventDatum.durationMinutes;
    return accountDataDurationMinutes || event.durationMinutes;
  } else {
    return null;
  }
}

export function tagEventChanges(oldEvent, newEvent) {
  const attendeesChanged = !_.isEqual(extractFlaggingUsers(oldEvent), extractFlaggingUsers(newEvent));
  const colorChanged = !_.isEqual(extractColor(oldEvent), extractColor(newEvent));
  const durationMinutesChanged = !_.isEqual(extractDurationMinutes(oldEvent), extractDurationMinutes(newEvent));
  const oldLoc = extractLocation(oldEvent);
  const newLoc = extractLocation(newEvent);
  const locationChanged = !_.isEqual(oldLoc, newLoc);
  const locationChangeDistance = locationChanged ? rateLocationChange(oldLoc, newLoc) : null;
  const nameChanged = !_.isEqual(oldEvent.name, newEvent.name);
  const noteChanged = !_.isEqual(extractGameNote(oldEvent), extractGameNote(newEvent));
  const recruitsChanged = !_.isEqual(extractRecruits(oldEvent), extractRecruits(newEvent));
  const scheduledChanged = !_.isEqual(extractScheduled(oldEvent), extractScheduled(newEvent));

  return {
    attendeesChanged,
    colorChanged,
    durationMinutesChanged,
    locationChangeDistance,
    locationChanged,
    nameChanged,
    noteChanged,
    recruitsChanged,
    scheduledChanged
  };
}

export function isGameComplete({ sides: { home, visitor } }) {
  return home && visitor &&
    home.score && typeof home.score.score === 'number' &&
    visitor.score && typeof visitor.score.score === 'number' &&
    home.team && visitor.team;
}

export function hasSameAddress(location1, location2) {
  const isAddressTheSame = location1.address.address1.toLowerCase() === location2.address.address1.toLowerCase();
  const isPostalCodeTheSame = location1.address.postalCode === location2.address.postalCode;

  return isAddressTheSame && isPostalCodeTheSame;
}

export function getDefaultScheduled({ fromDate, toDate }) {
  const { ISO_8601 } = dateFormats;
  const isSingleDay = fromDate === toDate;
  const fromMoment = moment(fromDate, ISO_8601);
  const toMoment = moment(toDate, ISO_8601);
  const now = moment();

  if (isSingleDay) {
    const today = moment().format(ISO_8601);
    const isToday = fromDate === today;

    return isToday ? now.startOf('hour').add(1, 'hour').valueOf() : fromMoment.startOf('day').valueOf();
  } else {
    const rangeContainsCurrentDay = (fromMoment.isSameOrBefore(now) && toMoment.isSameOrAfter(now));

    return rangeContainsCurrentDay ? now.startOf('hour').add(1, 'hour').valueOf() : fromMoment.startOf('day').valueOf();
  }
}



// WEBPACK FOOTER //
// ./src/js/util/EventUtil.js